import { PRACTICE_FIELD_OPTIONS } from '../../practice/details/fieldDefinitions.ts';
import type { CreateOrthodontistBody } from '../../../../services/orthodontistAccounts';

// Flat counterpart to practice/details/fieldDefinitions.ts's PracticeFieldDef -
// no `block`, since POST /orthodontist's body isn't block-structured.
export type OrthodontistFieldType = 'text' | 'tel' | 'email' | 'select';

export interface OrthodontistFieldDef {
    key: keyof CreateOrthodontistBody;
    label: string;
    type: OrthodontistFieldType;
    /** Grid columns in the 12-column field grid - see fieldDefinitions.ts's `span`. */
    span: number;
    maxLength: number;
    required?: boolean;
    mono?: boolean;
}

// Row spans sum to 12, same layout convention as PRACTICE_DETAIL_GROUPS.
export const ORTHODONTIST_CREATE_FIELDS: OrthodontistFieldDef[] = [
    {
        key: 'name',
        label: 'Organization name',
        type: 'text',
        span: 5,
        maxLength: 300,
        required: true
    },
    { key: 'phone', label: 'Phone', type: 'tel', span: 3, maxLength: 50 },
    { key: 'email', label: 'Email', type: 'email', span: 4, maxLength: 320 },
    {
        key: 'drFirstName',
        label: 'Doctor first name',
        type: 'text',
        span: 4,
        maxLength: 200,
        required: true
    },
    {
        key: 'drLastName',
        label: 'Doctor last name',
        type: 'text',
        span: 4,
        maxLength: 200,
        required: true
    },
    { key: 'drEmail', label: 'Doctor email', type: 'email', span: 4, maxLength: 320 },
    { key: 'address', label: 'Street address', type: 'text', span: 9, maxLength: 500 },
    { key: 'addressDetails', label: 'Suite / unit', type: 'text', span: 3, maxLength: 500 },
    { key: 'city', label: 'City', type: 'text', span: 4, maxLength: 200 },
    { key: 'state', label: 'State', type: 'select', span: 3, maxLength: 100 },
    { key: 'zip', label: 'ZIP', type: 'text', span: 2, maxLength: 20, mono: true },
    { key: 'country', label: 'Country', type: 'select', span: 3, maxLength: 100 }
];

export const ORTHODONTIST_REQUIRED_FIELDS = ORTHODONTIST_CREATE_FIELDS.filter(
    (field) => field.required
);

const ORTHODONTIST_FIELD_OPTIONS: Partial<Record<keyof CreateOrthodontistBody, string[]>> = {
    state: PRACTICE_FIELD_OPTIONS.state,
    country: PRACTICE_FIELD_OPTIONS.country
};

export function resolveOrthodontistFieldOptions(field: OrthodontistFieldDef): string[] {
    return field.type === 'select' ? (ORTHODONTIST_FIELD_OPTIONS[field.key] ?? []) : [];
}

export function emptyOrthodontistFieldValues(): Record<string, string> {
    return Object.fromEntries(ORTHODONTIST_CREATE_FIELDS.map((field) => [field.key, '']));
}

export function requiredOrthodontistFieldError(
    field: OrthodontistFieldDef,
    value: string
): string | null {
    if (!field.required) return null;
    return value.trim() === '' ? "can't be empty" : null;
}

// Drops every empty optional field rather than sending null - matches
// createOrthodontistBody's `optional()` (not `nullable()`-required) contract.
export function buildOrthodontistCreateBody(
    values: Record<string, string>
): CreateOrthodontistBody {
    const body: Record<string, string> = {};
    for (const field of ORTHODONTIST_CREATE_FIELDS) {
        const trimmed = (values[field.key] ?? '').trim();
        if (trimmed === '') continue;
        body[field.key] = trimmed;
    }
    return body as unknown as CreateOrthodontistBody;
}
